// A column header you can order the table by.
//
// Both registers - issues and Document Control - already sent `sort` and
// `sort_dir` to the server and both hardcoded them to "newest first". The
// column headings sat above the answer without being able to change it,
// so re-ordering meant a filter, an export, or scrolling.
//
//   const s = useTableSort("updatedAt", -1);
//   <SortableTh field="title" {...s.props}>Title</SortableTh>
//
// Columns that the server cannot order by (a preview image, a row of
// buttons, a list of people) simply pass no `field` and stay inert -
// a heading that looks clickable and does nothing is worse than one
// that never invited the click.
import React, { useState } from "react";
import { usePreference } from "helpers/userPrefs";

// `rememberAs`: a preference key - the sort is then remembered per person
// (helpers/userPrefs), e.g. useTableSort("updatedAt", -1, "issues.sort").
export function useTableSort(defaultField, defaultDir = -1, rememberAs = "") {
  const [local, setLocal] = useState({ field: defaultField, dir: defaultDir });
  const [saved, setSaved] = usePreference(rememberAs || "tableSort.unremembered", null);
  const cur = rememberAs && saved && saved.field ? saved : local;
  const field = cur.field;
  const dir = cur.dir === 1 ? 1 : -1;
  const put = (next) => { setLocal(next); if (rememberAs) setSaved(next); };
  const setDir = (fn) => put({ field, dir: typeof fn === "function" ? fn(dir) : fn });
  // First click on a new column sorts DESCENDING for dates and numbers
  // (newest, biggest, most urgent first - what people mean by "sort by
  // date") and ascending for everything else; clicking the active column
  // flips it.
  const onSort = (next, { numeric = false } = {}) => {
    if (String(next) === String(field)) { setDir((d) => (d === 1 ? -1 : 1)); return; }
    put({ field: next, dir: numeric ? -1 : 1 });
  };
  return { field, dir, onSort, props: { sort: field, dir, onSort } };
}

export default function SortableTh({ field, sort, dir, onSort, numeric = false, children, style, title }) {
  const sortable = !!field && typeof onSort === "function";
  const active = sortable && String(sort) === String(field);
  return (
    <th
      style={{ ...(style || {}), whiteSpace: "nowrap", cursor: sortable ? "pointer" : undefined, userSelect: "none" }}
      onClick={sortable ? () => onSort(field, { numeric }) : undefined}
      title={title || (sortable ? "Sort by this column" : undefined)}
    >
      {children}
      {sortable && (
        <span
          aria-hidden
          style={{ marginLeft: 6, fontSize: 9, opacity: active ? 1 : 0.3, verticalAlign: "middle" }}
        >
          {active ? (dir === 1 ? "▲" : "▼") : "▴"}
        </span>
      )}
    </th>
  );
}
